import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "previewContainer", "previewGrid"]

  connect() {
    // Store selected files with their preview data
    this.selectedFiles = []
    this.filePreviews = new Map() // Map to store file -> preview data
  }

  preview(event) {
    const newFiles = Array.from(event.target.files)
    
    if (newFiles.length === 0) {
      return
    }

    // Add new files to the selected files array
    newFiles.forEach((file) => {
      if (file.type.startsWith("image/")) {
        // Check if file is not already in the list (by name and size)
        const isDuplicate = this.selectedFiles.some(
          (f) => f.name === file.name && f.size === file.size
        )
        if (!isDuplicate) {
          this.selectedFiles.push(file)
        }
      }
    })

    // Update the input's FileList
    this.updateInputFiles()

    // Show preview container
    this.previewContainerTarget.classList.remove("hidden")

    // Create preview for new files
    newFiles.forEach((file) => {
      if (!file.type.startsWith("image/")) {
        return
      }

      // Check if preview already exists
      if (this.filePreviews.has(file)) {
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const previewData = {
          file: file,
          dataUrl: e.target.result
        }
        this.filePreviews.set(file, previewData)
        this.renderPreview(file, previewData)
      }
      reader.readAsDataURL(file)
    })
  }

  removeFile(event) {
    const fileIndex = parseInt(event.currentTarget.dataset.fileIndex)
    const file = this.selectedFiles[fileIndex]
    
    // Remove from arrays
    this.selectedFiles.splice(fileIndex, 1)
    this.filePreviews.delete(file)

    // Update the input's FileList
    this.updateInputFiles()

    // Re-render all previews
    this.renderAllPreviews()

    // Hide container if no files left
    if (this.selectedFiles.length === 0) {
      this.previewContainerTarget.classList.add("hidden")
    }
  }

  renderPreview(file, previewData) {
    const fileIndex = this.selectedFiles.indexOf(file)
    const previewItem = document.createElement("div")
    previewItem.className = "relative aspect-video rounded-lg overflow-hidden group"
    previewItem.dataset.fileIndex = fileIndex
    
    const img = document.createElement("img")
    img.src = previewData.dataUrl
    img.className = "w-full h-full object-cover"
    img.alt = `Preview ${fileIndex + 1}`
    
    // Delete button
    const deleteButton = document.createElement("button")
    deleteButton.type = "button"
    deleteButton.className = "absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity shadow-lg z-10"
    deleteButton.dataset.fileIndex = fileIndex
    deleteButton.setAttribute("data-action", "click->photo-preview#removeFile")
    deleteButton.setAttribute("title", "Supprimer cette photo")
    deleteButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 6h18"/>
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
        <line x1="10" y1="11" x2="10" y2="17"/>
        <line x1="14" y1="11" x2="14" y2="17"/>
      </svg>
    `
    
    previewItem.appendChild(img)
    previewItem.appendChild(deleteButton)
    this.previewGridTarget.appendChild(previewItem)
  }

  renderAllPreviews() {
    // Clear all previews
    this.previewGridTarget.innerHTML = ""

    // Re-render all files with updated indices
    this.selectedFiles.forEach((file, index) => {
      const previewData = this.filePreviews.get(file)
      if (previewData) {
        const previewItem = document.createElement("div")
        previewItem.className = "relative aspect-video rounded-lg overflow-hidden group"
        previewItem.dataset.fileIndex = index
        
        const img = document.createElement("img")
        img.src = previewData.dataUrl
        img.className = "w-full h-full object-cover"
        img.alt = `Preview ${index + 1}`
        
        // Delete button
        const deleteButton = document.createElement("button")
        deleteButton.type = "button"
        deleteButton.className = "absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity shadow-lg z-10"
        deleteButton.dataset.fileIndex = index
        deleteButton.setAttribute("data-action", "click->photo-preview#removeFile")
        deleteButton.setAttribute("title", "Supprimer cette photo")
        deleteButton.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 6h18"/>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
        `
        
        previewItem.appendChild(img)
        previewItem.appendChild(deleteButton)
        this.previewGridTarget.appendChild(previewItem)
      }
    })
  }

  updateInputFiles() {
    // Create a new FileList using DataTransfer
    const dataTransfer = new DataTransfer()
    this.selectedFiles.forEach((file) => {
      dataTransfer.items.add(file)
    })
    
    // Update the input's files
    this.inputTarget.files = dataTransfer.files
  }
}

